import {v4 as uuid} from 'uuid';
import {STATIC_TABLE_DEFAULT_CELL_VALUE, statusDefaultMainPic} from '../constants';
import i18n from '../i18n';
import {IFont} from '../incase-domain/common/font';
import {ImageFitEnum} from '../incase-domain/common/imageFit';
import {IElement} from '../incase-domain/elements';
import {ELEMENT} from '../incase-domain/elements/base/ElementTypes';
import {IElementButton} from '../incase-domain/elements/button';
import {IElementCalculator} from '../incase-domain/elements/calculator';
import {FLIP_CARD_SIDE, IElementFlipCard} from '../incase-domain/elements/flipCard';
import {IElementForm} from '../incase-domain/elements/form';
import {IElementHtml} from '../incase-domain/elements/html';
import {IElementImage} from '../incase-domain/elements/image';
import {IElementImageGallery} from '../incase-domain/elements/imageGallery';
import {IElementModal} from '../incase-domain/elements/modal';
import {IElementPlaceholder} from '../incase-domain/elements/placeholder';
import {IElementImageMarkup} from '../incase-domain/elements/question/imageMarkup';
import {IElementLikertScale} from '../incase-domain/elements/question/likertScale';
import {IElementMultiSelect} from '../incase-domain/elements/question/multiSelect';
import {IElementPhotoAnswerQuestion} from '../incase-domain/elements/question/photoAnswerQuestion';
import {IElementQuestion} from '../incase-domain/elements/question/question';
import {IElementQuestionMatchPair} from '../incase-domain/elements/question/questionMatchPair';
import {IQuestionReportElement} from '../incase-domain/elements/question/questionReportElement';
import {IElementSequenceQuestion} from '../incase-domain/elements/question/questionSequence';
import {IElementArrow} from '../incase-domain/elements/shapes/arrow';
import {IElementCircle} from '../incase-domain/elements/shapes/circle';
import {IElementLine} from '../incase-domain/elements/shapes/line';
import {IElementRectangle} from '../incase-domain/elements/shapes/rectangle';
import {IElementStar} from '../incase-domain/elements/shapes/star';
import {IElementTriangle} from '../incase-domain/elements/shapes/triangle';
import {IElementStaticList} from '../incase-domain/elements/staticList';
import {IElementStaticTableV2} from '../incase-domain/elements/staticTableV2';
import {IElementStaticText} from '../incase-domain/elements/staticText';
import {IElementStatusComponent} from '../incase-domain/elements/statusComponent';
import {IElementTextInput} from '../incase-domain/elements/textInput';
import {IElementVideo, VideoSourceType} from '../incase-domain/elements/video';
import {Font} from '../incase-domain/presentationTheme';
import {store} from '../store/store';
import {defaultFonts} from '../utils/defaultFonts';
import {getPresentationThemeForEditor} from '../utils/presentationThemeFromCache';
import {setTextColor} from '../utils/presentationTheme';
import {answersCreator, createYesNoAnswers} from './utils';
import {IElementQuestionYesNoV2} from '../incase-domain/elements/question/questionYesNoV2';
import {IElementLineV2} from '../incase-domain/elements/shapes/lineV2';
import {IElementArrowV2} from '../incase-domain/elements/shapes/arrowV2';
import {IElementChatbot} from '../incase-domain/elements/chatbot';
import {IElementEllipse} from '../incase-domain/elements/shapes/ellipse';

export type ElementByType<T extends ELEMENT> = Extract<IElement, {type: T}>;
export type ElementProps<T extends ELEMENT> = Partial<Omit<ElementByType<T>, 'id' | 'slideId' | 'type'>>;

export const getEmptyElement = <T extends ELEMENT>(slideId: string, type: T, newElementProps?: ElementProps<T>): ElementByType<T> => {
    const {selectedPresentation} = store.getState().presentationReducer;
    const theme = getPresentationThemeForEditor(selectedPresentation.presentationThemeId);

    const bkgColorPurple = '#41088A';
    const fontWhite = (size = 2): {font: IFont} => ({font: {color: '#fff', size}});
    const fontBlack = (size = 2): {font: IFont} => setTextColor(size, theme, selectedPresentation);

    const questionFontSize = theme.elementStyles?.questionElement?.questionFontSize?.value;
    const answerFontSize = theme.elementStyles?.questionElement?.answerFontSize?.value;
    const commentFontSize = theme.elementStyles?.questionElement?.commentFontSize?.value;

    let defaultThemeFont: Font = {name: 'Roboto', url: 'Roboto'};

    if (selectedPresentation.presentationThemeId && (theme.fonts || theme.defaultFonts)) {
        const allFonts = (theme.fonts || []).concat(theme.defaultFonts ? theme.defaultFonts : defaultFonts);
        const selectedFont = allFonts.find((font) => font.selected);

        if (selectedFont) {
            defaultThemeFont = selectedFont;
        }
    }

    const uuidA1 = uuid();
    const uuidA2 = uuid();
    const uuidB1 = uuid();
    const uuidB2 = uuid();

    const confirmButtonLabel = 'Confirm';

    let emptyElement = {
        xPosition: Math.random() * 15,
        yPosition: Math.random() * 15,
        width: '15%',
        height: '10%',
        fitContent: true,
        type,
        slideId,
        id: `${slideId}-${uuid()}`,
        angle: 0,
    } as IElement;

    switch (type) {
        case ELEMENT.PLACEHOLDER:
            emptyElement = {
                ...emptyElement,
            } as IElementPlaceholder;
            break;
        case ELEMENT.STATIC_TEXT:
            emptyElement = {
                ...emptyElement,
                ...fontBlack(1.5),
                text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.text')}`,
                width: '25%',
                height: '15%',
                slideChange: {
                    slideId: '',
                    name: '',
                },
                ...(selectedPresentation?.presentationThemeId &&
                    theme.elementStyles?.textElement?.borderRadius && {
                        borderRadius: theme.elementStyles.textElement.borderRadius,
                    }),
                ...(selectedPresentation?.presentationThemeId &&
                    theme.elementStyles?.textElement?.backgroundColor && {
                        color: theme.elementStyles.textElement.backgroundColor,
                    }),
                fontFamily: defaultThemeFont,
            } as IElementStaticText;
            break;
        case ELEMENT.HTML:
            emptyElement = {
                ...emptyElement,
                text: '<div style="font-size: 1.5em; color: gray">Enter HTML code <strong>here</strong> </div>',
                width: '20%',
                height: '15%',
                slideChange: {
                    slideId: '',
                    name: '',
                },
                fontFamily: defaultThemeFont,
            } as IElementHtml;
            break;
        case ELEMENT.STATIC_LIST:
            emptyElement = {
                ...emptyElement,
                ...fontBlack(1.5),
                width: '20%',
                height: '15%',
                items: [`${i18n.t('presEditPage.editSidebar.elements.initialStates.list')}`],
                fontFamily: defaultThemeFont,
            } as IElementStaticList;
            break;
        case ELEMENT.STATIC_TABLE_V2:
            emptyElement = {
                ...emptyElement,
                ...fontBlack(1.5),
                width: '65%',
                height: '15%',
                text: `
                <table>
                    <tbody>
                      <tr>
                        <th>${STATIC_TABLE_DEFAULT_CELL_VALUE}</th>
                        <th>${STATIC_TABLE_DEFAULT_CELL_VALUE}</th>
                        <th>${STATIC_TABLE_DEFAULT_CELL_VALUE}</th>
                      </tr>
                      <tr>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                      </tr>
                      <tr>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                        <td>${STATIC_TABLE_DEFAULT_CELL_VALUE}</td>
                      </tr>
                    </tbody>
                </table>
                `,
            } as IElementStaticTableV2;
            break;
        case ELEMENT.IMAGE:
            emptyElement = {
                ...emptyElement,
                width: '30%',
                imageSrc: '',
                slideChange: {
                    slideId: '',
                    name: '',
                },
            } as IElementImage;

            if (newElementProps) {
                emptyElement = {
                    ...emptyElement,
                    ...newElementProps,
                };
            }

            break;
        case ELEMENT.IMAGE_GALLERY:
            emptyElement = {
                ...emptyElement,
                images: [],
                numberOfImagesInARow: 3,
                imageMargin: 0.1,
                width: '35%',
                height: '15%',
            } as IElementImageGallery;
            break;
        case ELEMENT.VIDEO:
            emptyElement = {...emptyElement, videoSource: {src: '', type: VideoSourceType.url}} as IElementVideo;
            if (newElementProps) {
                emptyElement = {
                    ...emptyElement,
                    ...newElementProps,
                };
            }
            break;
        case ELEMENT.QUESTION:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                answers: answersCreator(2),
                correctAnswers: [],
                blockingQuestion: true,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1.2,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.2,
                singleCorrect: false,
                writeAnswerIndices: true,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
                fontFamily: defaultThemeFont,
                showInReport: true,
                boxShadow: 2,
            } as IElementQuestion;
            break;
        case ELEMENT.PHOTO_ANSWER_QUESTION:
            emptyElement = {
                ...emptyElement,
                height: '40%',
                width: '40%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                answers: answersCreator(2),
                correctAnswers: [],
                blockingQuestion: true,
                imageFit: ImageFitEnum.COVER,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                answersInARow: 3,
                singleCorrect: false,
                writeAnswerIndices: true,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
                fontFamily: defaultThemeFont,
                showInReport: true,
            } as IElementPhotoAnswerQuestion;
            break;
        case ELEMENT.IMAGE_MARKUP:
            emptyElement = {
                ...emptyElement,
                width: '30%',
                imageSrc: '',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                markedAreas: [],
                intersectionAnswerType: false,
                correctAnswerMinIntersection: 0.5,
                blockingQuestion: true,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                singleCorrect: false,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
                fontFamily: defaultThemeFont,
                showInReport: true,
            } as IElementImageMarkup;

            if (newElementProps) {
                emptyElement = {
                    ...emptyElement,
                    ...newElementProps,
                };
            }

            break;
        case ELEMENT.YES_NO_QUESTION_V2:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                answers: createYesNoAnswers(),
                correctAnswers: [],
                blockingQuestion: true,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1.2,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.2,
                singleCorrect: true,
                writeAnswerIndices: false,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
                fontFamily: defaultThemeFont,
                showInReport: true,
                boxShadow: 2,
            } as IElementQuestionYesNoV2;
            break;
        case ELEMENT.SEQUENCE_QUESTION:
            emptyElement = {
                ...emptyElement,
                height: '15%',
                width: '25%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.sequence')}`,
                sequence: [
                    {text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.sequenceItem')}`, comment: ''},
                    {text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.sequenceItem')} 1`, comment: ''},
                ],
                correctSequence: [
                    `${i18n.t('presEditPage.editSidebar.elements.initialStates.sequenceItem')}`,
                    `${i18n.t('presEditPage.editSidebar.elements.initialStates.sequenceItem')} 1`,
                ],
                blockingQuestion: true,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                fontFamily: defaultThemeFont,
                showInReport: true,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
                automaticShuffle: true,
            } as IElementSequenceQuestion;
            break;
        case ELEMENT.MULTI_SELECT:
            emptyElement = {
                ...emptyElement,
                font: {size: 1, color: '#000000'},
                width: '25%',
                height: '15%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1.2,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                answers: answersCreator(2),
                haveTryButton: true,
                tryLabel: confirmButtonLabel,
                fitContent: true,
                fontFamily: defaultThemeFont,
                showCorrect: true,
                showWrong: true,
                showCorrectCount: true,
                showWrongCount: true,
                showInReport: true,
            } as IElementMultiSelect;
            break;
        case ELEMENT.LIKERT_SCALE:
            emptyElement = {
                ...emptyElement,
                width: '35%',
                height: '15%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.likertScale')}`,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 1,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                scaleSize: 5,
                showInReport: true,
                questions: [
                    {id: uuid(), text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`},
                    {id: uuid(), text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')} 2`},
                ],
                scale: [
                    {id: uuid(), text: '1'},
                    {id: uuid(), text: '2'},
                    {id: uuid(), text: '3'},
                    {id: uuid(), text: '4'},
                    {id: uuid(), text: '5'},
                ],
                fitContent: true,
                haveTryButton: true,
                tryLabel: confirmButtonLabel,
                sliderView: true,
            } as IElementLikertScale;
            break;
        case ELEMENT.QUESTION_REPORT:
            emptyElement = {
                ...emptyElement,
                graphSize: 'md',
                width: '30%',
                height: '15%',
                fontFamily: defaultThemeFont,
            } as IQuestionReportElement;
            break;
        case ELEMENT.MATCH_PAIR_QUESTION:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                question: `${i18n.t('presEditPage.editSidebar.elements.initialStates.question')}`,
                blockingQuestion: true,
                fontSize:
                    selectedPresentation?.presentationThemeId && answerFontSize
                        ? theme.elementStyles?.questionElement?.answerFontSize?.value
                        : 1,
                commentFontSize:
                    selectedPresentation?.presentationThemeId && commentFontSize
                        ? theme.elementStyles?.questionElement?.commentFontSize?.value
                        : 0.9,
                questionFontSize:
                    selectedPresentation?.presentationThemeId && questionFontSize
                        ? theme.elementStyles?.questionElement?.questionFontSize?.value
                        : 1.4,
                leftElement: [
                    {id: uuidA1, text: 'Element A1'},
                    {id: uuidA2, text: 'Element A2'},
                ],
                rightElement: [
                    {id: uuidB1, text: 'Element B1'},
                    {id: uuidB2, text: 'Element B2'},
                ],
                pairs: [
                    {leftElementId: uuidA1, rightElementId: uuidB1},
                    {leftElementId: uuidA2, rightElementId: uuidB2},
                ],
                fontFamily: defaultThemeFont,
                showInReport: true,
                haveTryButton: true,
                fitContent: true,
                tryLabel: confirmButtonLabel,
            } as IElementQuestionMatchPair;
            break;
        case ELEMENT.RECTANGLE:
        case ELEMENT.CIRCLE:
        case ELEMENT.ELLIPSE:
        case ELEMENT.TRIANGLE:
        case ELEMENT.STAR:
            emptyElement = {
                ...emptyElement,
                fill: false,
                width: '10%',
                height: '15%',
                slideChange: {
                    slideId: '',
                    name: '',
                },
                fitContent: false,
            } as IElementRectangle | IElementCircle | IElementEllipse | IElementTriangle | IElementStar;
            break;
        case ELEMENT.LINE:
        case ELEMENT.ARROW:
            emptyElement = {
                ...emptyElement,
                width: '100%',
                height: '100%',
                slideChange: {
                    slideId: '',
                    name: '',
                },
            } as IElementLine | IElementArrow;
            break;
        case ELEMENT.LINE_V2:
            emptyElement = {
                ...emptyElement,
                width: '100%',
                height: '100%',
                fitContent: false,
                startPoint: {x: 0.1, y: 0.2},
                endPoint: {x: 0.2, y: 0.2},
                slideChange: {
                    slideId: '',
                    name: '',
                },
            } as IElementLineV2;
            break;
        case ELEMENT.ARROW_V2:
            emptyElement = {
                ...emptyElement,
                width: '100%',
                height: '100%',
                fitContent: false,
                startPoint: {x: 0.1, y: 0.2},
                endPoint: {x: 0.2, y: 0.2},
                slideChange: {
                    slideId: '',
                    name: '',
                },
            } as IElementArrowV2;
            break;
        case ELEMENT.STATUS:
            emptyElement = {
                ...emptyElement,
                ...fontBlack,
                statusMainImage: statusDefaultMainPic,
                mainImageWidthHeightRatio: 2.33,
                statusLayout: 0,
                width: '45%',
                height: '80%',
                statusParts: [
                    {
                        text: 'Status part 1',
                        image: '',
                    },
                    {
                        text: 'Status part 2',
                        image: '',
                    },
                    {
                        text: 'Status part 3',
                        image: '',
                    },
                ],
            } as IElementStatusComponent;
            break;
        case ELEMENT.TEXT_INPUT:
            emptyElement = {
                ...emptyElement,
                ...fontBlack(1.5),
                width: '25%',
                height: '15%',
                label: `${i18n.t('presEditPage.editSidebar.elements.initialStates.textInputLabel')}`,
                htmlName: 'inputName',
                placeholder: `${i18n.t('presEditPage.editSidebar.elements.initialStates.textInputPlaceholder')}`,
                fontFamily: defaultThemeFont,
            } as IElementTextInput;
            break;
        case ELEMENT.FORM:
            emptyElement = {
                ...emptyElement,
                title: 'Form',
                width: '25%',
                height: '15%',
                fieldElements: [getEmptyElement(slideId, ELEMENT.TEXT_INPUT), getEmptyElement(slideId, ELEMENT.BUTTON)],
                method: 'POST',
                fontFamily: defaultThemeFont,
                fontSize: 1.5,
            } as IElementForm;
            break;
        case ELEMENT.BUTTON:
            emptyElement = {
                ...emptyElement,
                ...fontWhite(1.8),
                color: bkgColorPurple,
                text: `${i18n.t('presEditPage.editSidebar.elements.initialStates.button')}`,
                slideChange: {
                    slideId: '',
                    name: '',
                },
                width: '20%',
                height: '15%',
                fontFamily: defaultThemeFont,
            } as IElementButton;
            break;
        case ELEMENT.MODAL:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                fontFamily: defaultThemeFont,
                elementToClickForDisplay: [],
                selectedSlideId: '',
            } as IElementModal;
            break;
        case ELEMENT.FLIP_CARD:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '40%',
                fitContent: false,
                flipCardOnHover: false,
                fontFamily: defaultThemeFont,
                flipCardElement: {
                    [FLIP_CARD_SIDE.FRONT]: getEmptyElement(slideId, ELEMENT.STATIC_TEXT, {name: FLIP_CARD_SIDE.FRONT}),
                    [FLIP_CARD_SIDE.BACK]: getEmptyElement(slideId, ELEMENT.STATIC_TEXT, {name: FLIP_CARD_SIDE.BACK}),
                },
            } as IElementFlipCard;
            break;
        case ELEMENT.CALCULATOR:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                calculatorId: '',
            } as IElementCalculator;
            break;
        case ELEMENT.CHATBOT:
            emptyElement = {
                ...emptyElement,
                width: '25%',
                height: '15%',
                header: '',
                profile: '',
                therapy: '',
                diagnosis: '',
            } as IElementChatbot;
            break;
        default:
            break;
    }

    if (newElementProps) {
        emptyElement = {...emptyElement, ...newElementProps};
    }

    const keepElementPositionedInSlideBounds = (element: ElementByType<T>): ElementByType<T> => {
        const elementWidth = parseFloat(element.width);
        const elementHeight = parseFloat(element.height);
        const {xPosition, yPosition} = element;

        const updatedElement = {...element};
        if (xPosition + elementWidth > 100) {
            updatedElement.xPosition = 100 - elementWidth;
        }
        if (yPosition + elementHeight > 100) {
            updatedElement.yPosition = 100 - elementHeight;
        }

        return updatedElement;
    };

    return keepElementPositionedInSlideBounds(emptyElement as ElementByType<T>);
};
